import { Box, Stack } from '@mui/material';
import { getBusinessContacts } from 'lib/api/contacts';
import { Contact } from 'lib/api/contacts/types';
import { preloadBusinessesGlobalSearcher } from 'lib/api/search';
import { PreloadedBusiness } from 'lib/api/search/types';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { errorHandler } from 'lib/functions/errors';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { SingleSelect } from '../../SingleSelect';
import { Aval } from '../types';

type Props = {
  aval: Aval;
  setAval: (aval: Aval) => void;
  value: Aval[];
};

const OtherCompany: React.FC<Props> = ({ aval, setAval, value }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [business, setBusiness] = useState<PreloadedBusiness | null>(null);
  const [businesses, setBusinesses] = useState<PreloadedBusiness[]>([]);
  const query = new URLSearchParams({
    isLegalRepresentative: 'true',
  }).toString();
  const { data, isPending } = getBusinessContacts(
    dispatch,
    business?.object?.identifier,
    query,
  );

  const contacts = data?.items ?? [];

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      const data = await preloadBusinessesGlobalSearcher(dispatch);
      if (data) setBusinesses(data);
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const getUserLabel = (user: Contact) => {
    if (!user.name) return '';
    const nameParts = user.name.split(' ').filter(Boolean);
    if (nameParts.length === 0) return '';
    if (nameParts.length === 1) return nameParts[0];
    return `${nameParts[0]} ${nameParts[1]}`;
  };

  const handleSelectOtherBusiness = (businessIdentifier: string) => {
    const business = businesses.find(
      (business) => business.value === businessIdentifier,
    );
    if (business) setBusiness(business);
  };

  const setOtherBusinessAval = (identifier: string) => {
    const signer = contacts.find((u) => u.identifier === identifier);
    if (signer) {
      const newAval = {
        ...aval,
        signer: {
          identifier: signer.identifier,
          name: signer.name,
          email: signer.email,
          phone: signer.phone,
          country: signer.country,
          profession: signer.profession,
          maritalStatus: signer.maritalStatus,
        },
        business: {
          name: business?.object?.name ?? '',
          identifier: business?.object?.identifier ?? '',
        },
      };
      setAval(newAval);
    }
  };

  const contactsOptions = useMemo(() => {
    const addedAvalIdentifiers = new Set(
      value
        .filter(
          (a) =>
            a.type === 'OTHER_COMPANY' &&
            a.signer?.identifier &&
            a.signer.identifier !== '',
        )
        .map((a) => a.signer!.identifier!),
    );
    return contacts
      .filter((contact): contact is Contact & { identifier: string } =>
        Boolean(contact.identifier),
      )
      .map((contact) => ({
        value: contact.identifier,
        label: getUserLabel(contact),
      }))
      .filter((option) => !addedAvalIdentifiers.has(option.value));
  }, [contacts, value]);

  useEffect(() => {
    void fetchBusinesses();
  }, []);

  if (loading || isPending)
    return (
      <Box height={100}>
        <CenteredCircularProgress type="dialog" />
      </Box>
    );

  return (
    <Stack direction="column" spacing={2}>
      <SingleSelect
        options={businesses}
        value={business?.value ?? ''}
        onChange={handleSelectOtherBusiness}
        label="Empresa aval"
      />
      <SingleSelect
        options={contactsOptions}
        value={aval?.signer?.identifier ?? ''}
        onChange={setOtherBusinessAval}
        label="Aval empresa"
      />
    </Stack>
  );
};

export default OtherCompany;
